import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useNavigate, useParams } from 'react-router'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { Spinner } from '@/components/ui/spinner'
import { CacheKeys } from '../../../lib/cache'
import {
  acceptInvitation,
  getInvitationDetails,
  type InvitationDetails,
  InvitationStatus,
} from '../../../models/invitation'

export function AcceptInvitation() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [invitation, setInvitation] = useState<InvitationDetails | null>(null)
  const [loading, setLoading] = useState(true)
  const [accepting, setAccepting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!id) return
    getInvitationDetails(id)
      .then(setInvitation)
      .catch(() => setError('Invitation not found or is no longer valid.'))
      .finally(() => setLoading(false))
  }, [id])

  async function handleAccept() {
    if (!id) return
    setAccepting(true)
    try {
      await acceptInvitation(id)
      await queryClient.invalidateQueries({ queryKey: [CacheKeys.CURRENT_USER] })
      toast.success(`Welcome to ${invitation?.orgName}!`)
      void navigate('/insure/insurance')
    } catch {
      setError('Failed to accept invitation. Please try again.')
    } finally {
      setAccepting(false)
    }
  }

  if (loading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Spinner className="size-8" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="mx-auto mt-20 max-w-md">
        <Card>
          <CardHeader>
            <CardTitle>Invitation Error</CardTitle>
            <CardDescription>{error}</CardDescription>
          </CardHeader>
          <CardFooter>
            <Button onClick={() => void navigate('/insure/insurance')}>Go to insurance</Button>
          </CardFooter>
        </Card>
      </div>
    )
  }

  if (!invitation || invitation.status !== InvitationStatus.PENDING) {
    return (
      <div className="mx-auto mt-20 max-w-md">
        <Card>
          <CardHeader>
            <CardTitle>Invalid Invitation</CardTitle>
            <CardDescription>This invitation is no longer valid.</CardDescription>
          </CardHeader>
          <CardFooter>
            <Button onClick={() => void navigate('/insure/insurance')}>Go to insurance</Button>
          </CardFooter>
        </Card>
      </div>
    )
  }

  return (
    <div className="mx-auto mt-20 max-w-md">
      <Helmet>
        <title>Accept Invitation</title>
      </Helmet>
      <Card>
        <CardHeader>
          <CardTitle>Join {invitation.orgName}</CardTitle>
          <CardDescription>
            You have been invited to join <strong>{invitation.orgName}</strong> as a{' '}
            <strong>{invitation.role}</strong>.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground text-sm">
            By accepting this invitation, you will become a member of this organization and it will be set as
            your active organization.
          </p>
        </CardContent>
        <CardFooter className="flex gap-3">
          <Button variant="outline" onClick={() => void navigate('/insure/insurance')}>
            Decline
          </Button>
          <Button onClick={handleAccept} disabled={accepting}>
            {accepting ? 'Accepting...' : 'Accept Invitation'}
          </Button>
        </CardFooter>
      </Card>
    </div>
  )
}
